import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a number as Turkish Lira currency
 * @param amount - The amount to format
 * @param options - Formatting options
 * @returns Formatted currency string (e.g., "4.000.000 TL")
 */
export function formatCurrency(
  amount: number | string,
  options: {
    showCurrency?: boolean
    currencySymbol?: string
    fractionDigits?: number
  } = {},
): string {
  const { showCurrency = true, currencySymbol = "TL", fractionDigits = 0 } = options;

  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) return "0";

  // Format with Turkish locale (. for thousands, , for decimals)
  const formatted = new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(numAmount);

  return showCurrency ? `${formatted} ${currencySymbol}` : formatted;
}

/**
 * Formats a phone number in international format
 * @param phone - The phone number to format
 * @returns Formatted phone string (e.g., "+90 532 123 45 67")
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return "";

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, "");

  // Format as +XX XXX XXX XX XX
  if (digits.length >= 11) {
    const countryCode = digits.slice(0, 2);
    const part1 = digits.slice(2, 5);
    const part2 = digits.slice(5, 8);
    const part3 = digits.slice(8, 10);
    const part4 = digits.slice(10, 12);

    return `+${countryCode} ${part1} ${part2} ${part3} ${part4}`;
  }

  return phone;
}

// Validate Turkish phone number (10 digits starting with 5)
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  // Remove spaces, formatting, and country code (+90 or 0)
  const cleaned = phoneNumber
    .replace(/[\s\-()]/g, "")
    .replace(/^\+90/, "")
    .replace(/^0/, "");

  // Check if it matches Turkish mobile format: 5XX XXX XX XX (10 digits starting with 5)
  return /^5\d{9}$/.test(cleaned);
};

// Format phone number to +XX XXX XXX XX XX
export const longFormatPhoneNumber = (phone: string): string => {
  if (!phone) return "";

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // Expected format: +XX XXX XXX XX XX
  if (cleaned.startsWith("+")) {
    const countryCode = cleaned.slice(0, 3); // +90
    const rest = cleaned.slice(3);

    if (rest.length === 10) {
      // Format: +XX XXX XXX XX XX
      return `${countryCode} ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6, 8)} ${rest.slice(8, 10)}`;
    }
  }

  return phone; // Return original if format doesn't match
};

// Truncate description helper
export const truncateDescription = (text: string, maxLength: number = 150) => {
  if (!text || text.length <= maxLength) return text;

  return text.slice(0, maxLength) + "...";
};

// Doğum tarihi validasyonu (dd.MM.yyyy formatı)
export const isValidBirthDate = (dateStr: string): boolean => {
  if (!dateStr || dateStr.length !== 10) return false;
  const parts = dateStr.split(".");
  if (parts.length !== 3) return false;
  const [day, month, year] = parts.map(Number);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
  if (day < 1 || day > 31 || month < 1 || month > 12) return false;
  if (year < 1900 || year > new Date().getFullYear() - 18) return false; // 18 yaş kontrolü

  return true;
};
